import { useElements, useStripe } from '@stripe/react-stripe-js';
import { IconButton } from '@youversion/design-system-components';
import ChevronLeftStroked from '@youversion/design-system-icons/lib/ChevronLeftStroked';
import ModalFooter from 'components/ui/modal/Footer';
import ModalHeader from 'components/ui/modal/Header';
import PaymentMethodsCard from 'components/views/giving/payment-methods-card';
import { useCreatePaymentMethod } from 'hooks/useCreatePaymentMethod';
import { useTranslation } from 'next-i18next';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

interface Props {
  header: Element | DocumentFragment | null;
  onAddCard: () => void;
  onBack: () => void;
}

export default function PaymentMethodAddCardDialog({ header, onAddCard, onBack }: Props) {
  const { t } = useTranslation('common');
  const stripe = useStripe();
  const elements = useElements();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const { createPaymentMethod, loading } = useCreatePaymentMethod({ onSuccess: onAddCard });

  const captchaTokenHandler = useCallback(
    (token: string | null) => {
      setCaptchaToken(token);
    },
    [setCaptchaToken]
  );

  const handleClick = () => {
    void createPaymentMethod(stripe, elements);
  };

  return (
    <>
      {header
        ? createPortal(
            <ModalHeader
              left={
                <IconButton className='inline-end-2 block-start-2' data-testid='modal-back-button' onClick={onBack}>
                  <span className='sr-only'>{t('giving.navigation.back')}</span>
                  <ChevronLeftStroked className='text-text-light dark:text-text-dark rtl:rotate-180' />
                </IconButton>
              }
              title={t('giving.titles.card')}
            />,
            header
          )
        : null}
      <div className='flex flex-col grow justify-between h-full pli-3 pbs-0.5'>
        <PaymentMethodsCard setCaptchaToken={captchaTokenHandler} />
      </div>
      <ModalFooter
        buttonText={t('giving.cta.done')}
        disabled={!captchaToken || loading}
        loading={loading}
        onClick={handleClick}
        testId='add-payment-method-done'
      />
    </>
  );
}
